import {
    HTMLAttributes,
    InputHTMLAttributes,
    ReactNode,
    useCallback,
    useMemo,
    useState
} from 'react';
import {
    Box,
    Card,
    Collapse,
    Grid2,
    LinearProgress,
    useTheme
} from '@mui/material';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { SemiBold, SubTitle, Title } from '@/typography/Typography';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { useErrorHandling } from '@/utils/errorHandling';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { DATE_FORMAT } from '@/lib/constants';
import { staticColors } from '@/lib/theme';
import { DASHBOARD_CARD_SURFACE } from '@/pages/dashboard/common';

const IMPORT_STATE = {
    IDLE: 'IDLE',
    REJECTED: 'REJECTED',
    SELECTED: 'SELECTED',
    UPLOADING: 'UPLOADING',
    DONE: 'DONE'
} as const;

type ImportState = (typeof IMPORT_STATE)[keyof typeof IMPORT_STATE];

/** Provider-agnostic view of an import response, for the shared result panel. */
export type ImportSummary = {
    total: number;
    updated: number;
    failed: number;
    errors: string[];
};

export type SalaryImportCardProps<TResponse> = {
    title: string;
    info: string;
    /** Shown inline once a file is picked, in place of the settings-page modal. */
    confirmationNote: string;
    accept: Accept;
    /** One line stating the constraints before a file is picked. */
    constraints: string;
    maxFileSizeBytes: number;
    upload: (
        file: File,
        onUploadProgress?: (percent: number) => void
    ) => Promise<{ data: TResponse }>;
    summarize: (response: TResponse) => ImportSummary;
    /** Full per-row detail, opened on demand from the inline result. */
    renderResultDialog: (args: {
        open: boolean;
        result: TResponse | null;
        onClose: () => void;
    }) => ReactNode;
    alerts: { success: string; partial: string; failed: string };
};

const BYTES_PER_MB = 1024 * 1024;

const formatMb = (bytes: number) => `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;

const formatFileSize = (bytes: number) =>
    bytes < BYTES_PER_MB
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : formatMb(bytes);

/**
 * Dashboard import control for salary systems with no API integration.
 *
 * Deliberately denser than the Salary Settings dropzone: the dashboard is a
 * glanceable surface, so the control collapses to a single row and spends the
 * space it saves on stating the constraints up front and reporting the result
 * in place. The per-row dialog stays reachable but is no longer the only chance
 * to read why rows failed — closing it used to discard the summary for good.
 *
 * Generic over the response shape because DataLon and DATEV LODAS return
 * different payloads; `summarize` narrows them to what the panel renders.
 */
export const SalaryImportCard = <TResponse,>({
    title,
    info,
    confirmationNote,
    accept,
    constraints,
    maxFileSizeBytes,
    upload,
    summarize,
    renderResultDialog,
    alerts
}: SalaryImportCardProps<TResponse>) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [state, setState] = useState<ImportState>(IMPORT_STATE.IDLE);
    const [file, setFile] = useState<File | null>(null);
    const [percent, setPercent] = useState(0);
    const [rejection, setRejection] = useState<string | null>(null);
    const [result, setResult] = useState<TResponse | null>(null);
    const [completedAt, setCompletedAt] = useState<string | null>(null);
    const [errorsOpen, setErrorsOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const { backendErrorHandler } = useErrorHandling();
    const { setAlert } = useNotificationsSnackbar();

    const reset = useCallback(() => {
        setState(IMPORT_STATE.IDLE);
        setFile(null);
        setPercent(0);
        setRejection(null);
        setResult(null);
        setCompletedAt(null);
        setErrorsOpen(false);
    }, []);

    const onDropAccepted = useCallback((accepted: File[]) => {
        setRejection(null);
        setFile(accepted[0]);
        setState(IMPORT_STATE.SELECTED);
    }, []);

    const onDropRejected = useCallback(
        (rejections: FileRejection[]) => {
            const rejected = rejections[0];
            const tooLarge = rejected?.errors.some(
                (e) => e.code === 'file-too-large'
            );

            setRejection(
                tooLarge
                    ? t('salary_import_file_too_large', {
                          size: formatMb(rejected.file.size),
                          limit: formatMb(maxFileSizeBytes)
                      })
                    : t('salary_import_file_type_rejected')
            );
            setFile(null);
            setState(IMPORT_STATE.REJECTED);
        },
        [maxFileSizeBytes, t]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept,
        maxSize: maxFileSizeBytes,
        multiple: false,
        // The row is the drop target, but clicking it opens the picker only via
        // the explicit button — otherwise Cancel/Import clicks reopen the dialog.
        noClick: state !== IMPORT_STATE.IDLE,
        onDropAccepted,
        onDropRejected
    });

    const handleImport = () => {
        if (file === null) {
            return;
        }

        setState(IMPORT_STATE.UPLOADING);
        setPercent(0);

        upload(file, setPercent)
            .then((response) => {
                const data = response.data;
                const summary = summarize(data);

                setResult(data);
                setCompletedAt(dayjs().format(DATE_FORMAT.DAY_MONTH_YEAR_TIME));
                setErrorsOpen(false);
                setState(IMPORT_STATE.DONE);

                if (summary.failed === 0 && summary.updated > 0) {
                    setAlert(alerts.success, 'success');
                } else if (summary.updated > 0) {
                    setAlert(alerts.partial, 'warning');
                } else {
                    setAlert(alerts.failed, 'error');
                }
            })
            .catch((error) => {
                backendErrorHandler()(error);
                reset();
            });
    };

    const summary = useMemo(
        () => (result === null ? null : summarize(result)),
        [result, summarize]
    );

    // A file whose rows were all skipped (no InstaPaid match, third-party account
    // holder) reports zero failures AND zero updates. Treating "no failures" as
    // success would show a green tick for an import that changed nothing, so the
    // clean state requires at least one row to have landed.
    const importedCleanly =
        summary !== null && summary.failed === 0 && summary.updated > 0;

    const resultHeadline =
        summary === null
            ? ''
            : importedCleanly
              ? t('salary_import_all_imported', { count: summary.updated })
              : summary.failed > 0
                ? t('salary_import_with_errors', { count: summary.failed })
                : t('salary_import_none_updated');

    // react-dropzone's prop types embed a duplicated @types/react csstype, so the
    // spread never structurally matches the element's own props even though the
    // runtime values are exactly what the element expects. Narrowing here keeps
    // the mismatch to one documented line instead of two opaque compile errors.
    const rootProps = getRootProps() as HTMLAttributes<HTMLDivElement>;
    const inputProps = getInputProps() as InputHTMLAttributes<HTMLInputElement>;

    const rowSx = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: '14px 16px',
        borderRadius: '8px'
    } as const;

    const glyphSx = (background: string, ring: string) => ({
        flex: 'none',
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        background,
        border: `4px solid ${ring}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    return (
        <Grid2 size={12}>
            {renderResultDialog({
                open: dialogOpen,
                result,
                onClose: () => setDialogOpen(false)
            })}

            <Card
                sx={{
                    borderRadius: DASHBOARD_CARD_SURFACE.borderRadius,
                    border: `1px solid ${DASHBOARD_CARD_SURFACE.borderColor}`,
                    boxShadow: DASHBOARD_CARD_SURFACE.boxShadow
                }}
            >
                <Box
                    display={'flex'}
                    flexDirection={'column'}
                    gap={theme.spacing(2)}
                    padding={theme.spacing(3)}
                >
                    <Box>
                        <Title>{title}</Title>
                        <SubTitle>{info}</SubTitle>
                    </Box>

                    <div {...rootProps} data-testid="salary-import-dropzone">
                        <input hidden type="file" {...inputProps} />

                        {(state === IMPORT_STATE.IDLE ||
                            state === IMPORT_STATE.REJECTED) &&
                            (isDragActive ? (
                                <Box
                                    data-testid="salary-import-dragover"
                                    sx={{
                                        ...rowSx,
                                        padding: '13px 15px',
                                        border: `2px dashed ${theme.palette.primary.main}`,
                                        background: '#F5FAFF'
                                    }}
                                >
                                    <Box
                                        sx={glyphSx('#D1E9FF', '#EFF8FF')}
                                        aria-hidden
                                    >
                                        <UploadGlyph
                                            color={theme.palette.primary.main}
                                        />
                                    </Box>
                                    <Box>
                                        <SemiBold
                                            color={theme.palette.primary.main}
                                        >
                                            {t('salary_import_drop_to_import')}
                                        </SemiBold>
                                        <SubTitle>
                                            {t(
                                                'salary_import_release_to_start'
                                            )}
                                        </SubTitle>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    data-testid="salary-import-idle"
                                    sx={{
                                        ...rowSx,
                                        border: `1px dashed ${theme.palette.greyBorder}`,
                                        background: '#FCFCFD'
                                    }}
                                >
                                    <Box
                                        sx={glyphSx('#F2F4F7', '#F9FAFB')}
                                        aria-hidden
                                    >
                                        <UploadGlyph color={'#606060'} />
                                    </Box>
                                    <Box minWidth={0}>
                                        <Box
                                            component={'span'}
                                            sx={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                color: theme.palette.secondary
                                                    .main
                                            }}
                                        >
                                            <Box
                                                component={'span'}
                                                sx={{
                                                    color: theme.palette.primary
                                                        .main,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {t('click_to_upload')}
                                            </Box>{' '}
                                            {t('or_drag_and_drop')}
                                        </Box>
                                        <SubTitle>{constraints}</SubTitle>
                                    </Box>
                                    <Box marginLeft={'auto'} flex={'none'}>
                                        <SecondaryButtonRegular
                                            onClick={open}
                                            data-testid="salary-import-choose"
                                        >
                                            {t('salary_import_choose_file')}
                                        </SecondaryButtonRegular>
                                    </Box>
                                </Box>
                            ))}

                        {state === IMPORT_STATE.SELECTED && file !== null && (
                            <Box
                                data-testid="salary-import-selected"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing(1.5),
                                    padding: '14px 16px',
                                    border: `1px solid ${theme.palette.greyBorder}`,
                                    borderRadius: '8px'
                                }}
                            >
                                <Box
                                    display={'flex'}
                                    alignItems={'center'}
                                    gap={theme.spacing(2)}
                                >
                                    <Box
                                        sx={glyphSx('#F5FAFF', '#F9FAFB')}
                                        aria-hidden
                                    >
                                        <FileGlyph
                                            color={theme.palette.primary.main}
                                        />
                                    </Box>
                                    <Box minWidth={0}>
                                        <SemiBold>{file.name}</SemiBold>
                                        <SubTitle>
                                            {formatFileSize(file.size)}
                                        </SubTitle>
                                    </Box>
                                    <Box
                                        marginLeft={'auto'}
                                        display={'flex'}
                                        gap={theme.spacing(1)}
                                    >
                                        <SecondaryButtonRegular onClick={reset}>
                                            {t('cancel')}
                                        </SecondaryButtonRegular>
                                        <PrimaryButton
                                            onClick={handleImport}
                                            data-testid="salary-import-confirm"
                                        >
                                            {t('salary_import_confirm')}
                                        </PrimaryButton>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        background: '#F9FAFB'
                                    }}
                                >
                                    <SubTitle>
                                        <SemiBold className="inline">
                                            {t('note')}:{' '}
                                        </SemiBold>
                                        {confirmationNote}
                                    </SubTitle>
                                </Box>
                            </Box>
                        )}

                        {state === IMPORT_STATE.UPLOADING && file !== null && (
                            <Box
                                data-testid="salary-import-uploading"
                                sx={{
                                    ...rowSx,
                                    border: `1px solid ${theme.palette.greyBorder}`
                                }}
                            >
                                <Box
                                    sx={glyphSx('#F5FAFF', '#F9FAFB')}
                                    aria-hidden
                                >
                                    <FileGlyph
                                        color={theme.palette.primary.main}
                                    />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <SemiBold>{file.name}</SemiBold>
                                    <SubTitle>
                                        {percent < 100
                                            ? t('salary_import_uploading', {
                                                  percent
                                              })
                                            : t('salary_import_finishing_up')}
                                    </SubTitle>
                                    <LinearProgress
                                        variant={
                                            percent < 100
                                                ? 'determinate'
                                                : 'indeterminate'
                                        }
                                        value={percent}
                                        sx={{
                                            mt: 1,
                                            height: '6px',
                                            borderRadius: '999px'
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {state === IMPORT_STATE.REJECTED &&
                            rejection !== null && (
                                <Box
                                    data-testid="salary-import-rejected"
                                    sx={{
                                        ...rowSx,
                                        mt: theme.spacing(1.5),
                                        border: '1px solid #FDA29B',
                                        background: staticColors.lightError
                                    }}
                                >
                                    <Box
                                        sx={glyphSx('#FEE4E2', '#FEF3F2')}
                                        aria-hidden
                                    >
                                        <ErrorGlyph
                                            color={theme.palette.text.error}
                                        />
                                    </Box>
                                    <Box minWidth={0}>
                                        <SemiBold>{rejection}</SemiBold>
                                        <SubTitle>
                                            {t('salary_import_rejected_hint')}
                                        </SubTitle>
                                    </Box>
                                </Box>
                            )}
                    </div>

                    {state === IMPORT_STATE.DONE && summary !== null && (
                        <Box
                            data-testid="salary-import-result"
                            sx={{
                                border: `1px solid ${theme.palette.greyBorder}`,
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    ...rowSx,
                                    borderRadius: 0,
                                    background: importedCleanly
                                        ? theme.palette.successBackground
                                        : '#F9FAFB'
                                }}
                            >
                                <Box
                                    sx={glyphSx(
                                        importedCleanly ? '#D1FADF' : '#FEF0C7',
                                        importedCleanly ? '#ECFDF3' : '#FFFCF5'
                                    )}
                                    aria-hidden
                                >
                                    {importedCleanly ? (
                                        <CheckGlyph
                                            color={theme.palette.text.success}
                                        />
                                    ) : (
                                        <WarningGlyph
                                            color={theme.palette.warning.main}
                                        />
                                    )}
                                </Box>
                                <Box minWidth={0}>
                                    <SemiBold>{resultHeadline}</SemiBold>
                                    <SubTitle>
                                        {[file?.name, completedAt]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </SubTitle>
                                </Box>
                                <Box
                                    marginLeft={'auto'}
                                    display={'flex'}
                                    gap={theme.spacing(1)}
                                    flex={'none'}
                                >
                                    <SecondaryButtonRegular
                                        onClick={() => setDialogOpen(true)}
                                        data-testid="salary-import-view-summary"
                                    >
                                        {t('salary_import_view_summary')}
                                    </SecondaryButtonRegular>
                                    <PrimaryButton
                                        onClick={reset}
                                        data-testid="salary-import-again"
                                    >
                                        {t('salary_import_upload_again')}
                                    </PrimaryButton>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    borderTop: `1px solid ${theme.palette.greyBorder}`
                                }}
                            >
                                <ResultStat
                                    label={t('total_rows')}
                                    value={summary.total}
                                    divider
                                />
                                <ResultStat
                                    label={t('salary_import_updated')}
                                    value={summary.updated}
                                    color={theme.palette.text.success}
                                    divider
                                />
                                <ResultStat
                                    label={t('failed_imports')}
                                    value={summary.failed}
                                    color={
                                        summary.failed > 0
                                            ? theme.palette.text.error
                                            : undefined
                                    }
                                />
                            </Box>

                            {summary.errors.length > 0 && (
                                <>
                                    <Box
                                        data-testid="salary-import-errors-toggle"
                                        onClick={() =>
                                            setErrorsOpen((prev) => !prev)
                                        }
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                            padding: '12px 16px',
                                            borderTop: `1px solid ${theme.palette.greyBorder}`,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                background: '#EFF8FF'
                                            }
                                        }}
                                    >
                                        <SemiBold
                                            color={theme.palette.text.error}
                                        >
                                            {t('salary_import_errors_found', {
                                                count: summary.errors.length
                                            })}
                                        </SemiBold>
                                    </Box>
                                    <Collapse in={errorsOpen}>
                                        <Box
                                            data-testid="salary-import-errors"
                                            sx={{
                                                borderTop: `1px solid ${theme.palette.greyBorder}`
                                            }}
                                        >
                                            {summary.errors.map((message) => (
                                                <Box
                                                    key={message}
                                                    sx={{
                                                        padding: '10px 16px',
                                                        borderBottom: `1px solid ${theme.palette.greySurface}`
                                                    }}
                                                >
                                                    <SubTitle>
                                                        {message}
                                                    </SubTitle>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </Card>
        </Grid2>
    );
};

const ResultStat = ({
    label,
    value,
    color,
    divider
}: {
    label: string;
    value: number;
    color?: string;
    divider?: boolean;
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                padding: '12px 16px',
                borderRight: divider
                    ? `1px solid ${theme.palette.greyBorder}`
                    : undefined
            }}
        >
            <SubTitle>{label}</SubTitle>
            <Box
                sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    color: color ?? theme.palette.text.primary
                }}
            >
                {value}
            </Box>
        </Box>
    );
};

const UploadGlyph = ({ color }: { color: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M14 3v4a1 1 0 0 0 1 1h4M5 21V3h9l6 6v12H5Z"
            stroke={color}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 16v-5m0 0-2 2m2-2 2 2"
            stroke={color}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const FileGlyph = ({ color }: { color: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M14 3v4a1 1 0 0 0 1 1h4M5 21V3h9l6 6v12H5Z"
            stroke={color}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CheckGlyph = ({ color }: { color: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="m5 13 4 4L19 7"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const WarningGlyph = ({ color }: { color: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M12 4 2.5 20h19L12 4Zm0 6v4m0 3h.01"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ErrorGlyph = ({ color }: { color: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
        <path
            d="M12 7.5v5m0 3h.01"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);
